import { FormattedMessage } from 'react-intl';
import { Icon } from '@ets/design-system';

import { Logger } from '_common/services';
import EditorManager from 'Editor/services/EditorManager';
import { DISPLAY_TYPES, ELEMENTS } from 'Editor/services/consts';
import styles from './EquationElement.module.scss';
import { BaseViewElement } from '../../BaseViewBuilder';
import { MMLHelper } from '_common/utils/MMLHelper';
import ReactUtils from '_common/utils/ReactUtils';
import { Root } from 'react-dom/client';
import { EditorDOMUtils } from 'Editor/services/_Common/DOM';

const EquationComponent = () => {
  return (
    <>
      {EditorDOMUtils.FormatZeroWidthSpace()}
      <div className={styles.errorContainer}>
        <div className={styles.wrapper}>
          <div className={styles.iconWrapper}>
            <Icon icon="triangle-exclamation" size="3" testId="warning" />
          </div>
          <span className={styles.contentWrapper}>
            <span className={styles.info}>
              <FormattedMessage id="EQUATION_ERROR" />
            </span>
          </span>
        </div>
      </div>
      {EditorDOMUtils.FormatZeroWidthSpace()}
    </>
  );
};

const hideOnClickOutside = (
  element: EquationElement,
  callback: (elem: EquationElement) => void,
) => {
  const outsideClickListener = (event: Event) => {
    if (!element.contains(event.target as Node) && isVisible(element)) {
      // or use: event.target.closest(selector) === null
      callback(element);
      removeClickListener();
    }
  };

  const removeClickListener = () => {
    document.removeEventListener('click', outsideClickListener);
  };

  document.addEventListener('click', outsideClickListener);
};

const isVisible = (elem: HTMLElement) =>
  !!elem && !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);

export class EquationElement extends BaseViewElement {
  private container?: HTMLElement;
  private rootReactContainer?: Root;

  static onDoubleClickListener() {
    EditorManager.getInstance().editEquation();
  }

  constructor() {
    super();
    this.onClickListener = this.onClickListener.bind(this);
  }

  get displayType() {
    return DISPLAY_TYPES.INLINE;
  }

  // Hook when element is mounted
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.onClickListener);
    this.addEventListener('dblclick', EquationElement.onDoubleClickListener);

    this.selected = false;
    // this.renderEquation();
  }

  preRender() {
    this.renderEquation();
    super.preRender();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    ReactUtils.unmountReactComponent(this.rootReactContainer);

    this.removeEventListener('click', this.onClickListener);
    this.removeEventListener('dblclick', EquationElement.onDoubleClickListener);
  }

  onClickListener() {
    EditorManager.getInstance().selectNodeContents(this);
    if (this.selected) {
      // this.selected = false;
    } else {
      this.selected = true;
      hideOnClickOutside(this, (element: EquationElement) => {
        element.selected = false;
      });
    }
  }

  get selected() {
    return this.hasAttribute('selected');
  }

  set selected(val) {
    if (val) {
      this.setAttribute('selected', 'true');
    } else {
      this.removeAttribute('selected');
    }
  }

  get equation() {
    return this.getAttribute('equation');
  }

  set equation(value: string | null) {
    if (value) {
      this.setAttribute('equation', value);
    }
  }

  updateEquation(value: string) {
    this.equation = value;
    this.renderEquation();
  }

  _setError() {
    if (!this.rootReactContainer) {
      this.rootReactContainer = ReactUtils.createReactRoot(this);
    }
    ReactUtils.renderReactComponent(<EquationComponent />, this.rootReactContainer);
  }

  renderEquation(recursive = false) {
    let isValid;
    while (this.firstChild) {
      this.firstChild.remove();
    }
    this.container = document.createElement('div');
    this.container.classList.add(styles.container);
    this.appendChild(document.createTextNode('\u200B'));
    this.appendChild(this.container);
    this.appendChild(document.createTextNode('\u200B'));

    try {
      MMLHelper.appendMMLToElement(this.equation, this.container);
      isValid = true;
    } catch (error) {
      if (!recursive) {
        // WARN: temp fix, some equations do not render properly the first time
        this.renderEquation(true);
      } else {
        isValid = false;
      }
      Logger.captureException(error);
    } finally {
      if (!isValid) {
        this._setError();
      }
    }
  }
}

if (!window.customElements.get(ELEMENTS.EquationElement.IDENTIFIER)) {
  window.customElements.define(ELEMENTS.EquationElement.IDENTIFIER, EquationElement);
}
